import { useState, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField,
  CircularProgress, Alert, Chip, Stack,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { vortxColors } from '@vortx/shared-ui';

const PHD_API_BASE = process.env.REACT_APP_PHD_API_URL || 'http://localhost:5002';

// Simple zip code → lat/lon lookup for common US regions
const ZIP_LOOKUP = {
  '94': { lat: 37.77, lon: -122.42 },   // SF Bay Area
  '90': { lat: 34.05, lon: -118.24 },   // Los Angeles
  '92': { lat: 32.72, lon: -117.16 },   // San Diego
  '60': { lat: 41.88, lon: -87.63 },    // Chicago
  '10': { lat: 40.71, lon: -74.01 },    // NYC
  '77': { lat: 29.76, lon: -95.37 },    // Houston
  '85': { lat: 33.45, lon: -112.07 },   // Phoenix
  '80': { lat: 39.74, lon: -104.99 },   // Denver
  '98': { lat: 47.61, lon: -122.33 },   // Seattle
  '33': { lat: 25.76, lon: -80.19 },    // Miami
};

function geocodeZip(zip) {
  if (!zip || zip.length < 2) return null;
  const prefix = zip.substring(0, 2);
  const match = ZIP_LOOKUP[prefix];
  if (match) return { latitude: match.lat, longitude: match.lon };
  // Default to center US if unknown
  return { latitude: 39.83, longitude: -98.58 };
}

import ProjectPicker from './ProjectPicker.jsx';

export default function LoadUploadStep({ onComplete, onProjectLoaded }) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [zipCode, setZipCode] = useState('');

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${PHD_API_BASE}/api/calibration/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `Upload failed (${response.status})`);
      }

      const result = await response.json();
      console.log('Upload result:', JSON.stringify(result).substring(0, 500));
      setUploadResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    handleFile(file);
  }, [handleFile]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback(() => setDragOver(false), []);

  const onFileInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    handleFile(file);
  }, [handleFile]);

  const handleConfirm = () => {
    if (!uploadResult) return;
    if (!zipCode || zipCode.length < 5) {
      setError('Please enter a 5-digit zip code');
      return;
    }
    const location = geocodeZip(zipCode);
    onComplete({
      load_8760_kw: uploadResult.load_8760_kw,
      quality: uploadResult.quality,
      location,
    });
  };

  // Down-sample 8760 to ~365 daily-peak points for charting
  const chartData = uploadResult?.load_8760_kw
    ? downsample(uploadResult.load_8760_kw)
    : [];

  const quality = uploadResult?.quality;

  return (
    <Box>
      {/* Load from existing project */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: vortxColors.white }}>
        Load from Existing Project
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: vortxColors.stone }}>
        Select a project to load its load profile, tariff, and asset configuration automatically.
      </Typography>
      <ProjectPicker onProjectLoaded={onProjectLoaded} />

      <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: vortxColors.stone + '30' }} />
        <Typography variant="caption" sx={{ mx: 2, color: vortxColors.stone }}>or upload a new CSV</Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: vortxColors.stone + '30' }} />
      </Box>

      {/* Upload zone */}
      <Paper
        variant="outlined"
        onClick={() => fileInputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        sx={{
          p: 5,
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: dragOver ? vortxColors.gold : vortxColors.stone,
          bgcolor: dragOver ? `${vortxColors.gold}10` : 'transparent',
          transition: 'all 0.2s ease',
          '&:hover': { borderColor: vortxColors.gold },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          hidden
          onChange={onFileInputChange}
        />
        {uploading ? (
          <CircularProgress size={40} sx={{ color: vortxColors.gold }} />
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 48, color: vortxColors.stone, mb: 1 }} />
            <Typography variant="body1" sx={{ color: vortxColors.stone }}>
              Drag &amp; drop a load CSV / Excel file, or click to browse
            </Typography>
            <Typography variant="caption" sx={{ color: vortxColors.stone, mt: 0.5 }}>
              8760 hourly load values (kW)
            </Typography>
          </>
        )}
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Upload result: chart + quality badges */}
      {uploadResult && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            8760 Load Profile
            {uploadResult.load_8760_kw && (
              <Typography component="span" variant="caption" sx={{ ml: 1, color: vortxColors.stone }}>
                ({uploadResult.load_8760_kw.length.toLocaleString()} values, peak: {Math.round(Math.max(...uploadResult.load_8760_kw)).toLocaleString()} kW)
              </Typography>
            )}
          </Typography>

          {chartData.length > 0 ? (
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(h) => {
                      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      const month = Math.floor(h / 730);
                      return months[Math.min(month, 11)];
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `${v.toFixed(0)}`}
                    label={{ value: 'kW', angle: -90, position: 'insideLeft', fontSize: 11 }}
                  />
                  <Tooltip
                    formatter={(v) => [`${v.toFixed(1)} kW`, 'Load']}
                    labelFormatter={(h) => `Hour ${h} (Day ${Math.floor(h/24)})`}
                  />
                  <Area
                    type="monotone"
                    dataKey="kw"
                    stroke={vortxColors.gold}
                    fill={vortxColors.gold}
                    fillOpacity={0.15}
                    strokeWidth={1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Alert severity="warning" sx={{ mt: 1 }}>
              No load data found in upload result. Check CSV format.
            </Alert>
          )}

          {/* Quality badges */}
          {quality && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <QualityBadge
                label={`${Math.min(quality.intact_pct || 0, 100).toFixed(1)}% intact`}
                good={(quality.intact_pct || 0) >= 95}
              />
              <QualityBadge
                label={`${quality.gaps_filled || 0} gaps filled`}
                good={(quality.gaps_filled || 0) === 0}
              />
              <QualityBadge
                label={`${quality.outliers_removed || 0} outliers removed`}
                good={(quality.outliers_removed || 0) === 0}
              />
            </Stack>
          )}

          {/* Location input — zip code */}
          <Stack direction="row" spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
            <TextField
              label="Site Zip Code"
              size="small"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
              sx={{ width: 160 }}
              placeholder="94105"
              inputProps={{ maxLength: 5 }}
            />
            {zipCode.length >= 2 && (
              <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                {(() => {
                  const loc = geocodeZip(zipCode);
                  return loc ? `${loc.latitude.toFixed(2)}, ${loc.longitude.toFixed(2)}` : '';
                })()}
              </Typography>
            )}
          </Stack>

          {/* Confirm button */}
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!uploadResult.load_8760_kw || zipCode.length < 5}
            startIcon={<CheckCircleIcon />}
            sx={{
              mt: 3,
              bgcolor: vortxColors.gold,
              '&:hover': { bgcolor: vortxColors.goldHover },
            }}
          >
            Confirm &amp; Continue
          </Button>
        </Box>
      )}
    </Box>
  );
}

function QualityBadge({ label, good }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: good ? `${vortxColors.success}20` : `${vortxColors.warning}20`,
        color: good ? vortxColors.success : vortxColors.warning,
        fontWeight: 500,
      }}
    />
  );
}

function downsample(arr) {
  if (!arr || arr.length === 0) return [];
  const points = [];
  for (let d = 0; d < 365; d++) {
    const start = d * 24;
    const end = Math.min(start + 24, arr.length);
    if (start >= arr.length) break;
    let peak = 0;
    for (let i = start; i < end; i++) {
      if (arr[i] > peak) peak = arr[i];
    }
    points.push({ hour: start, kw: peak });
  }
  return points;
}
